import React, { useState, useEffect, useRef } from "react";
import "./GridCommandAlive.scss";

/**
 * Grid Command Alive - The clean structure with gaming soul
 */
const GridCommandAlive: React.FC = () => {
    const [selectedGame, setSelectedGame] = useState<string>("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [showGameSelector, setShowGameSelector] = useState(false);
    const [pulseAnimation, setPulseAnimation] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Trigger pulse animation periodically
    useEffect(() => {
        const interval = setInterval(() => {
            setPulseAnimation(true);
            setTimeout(() => setPulseAnimation(false), 3000);
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    // Ambient particle system
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const particles: Array<{
            x: number;
            y: number;
            size: number;
            speedX: number;
            speedY: number;
            opacity: number;
        }> = [];

        // Create floating particles
        for (let i = 0; i < 50; i++) {
            particles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                size: Math.random() * 2 + 0.5,
                speedX: (Math.random() - 0.5) * 0.5,
                speedY: (Math.random() - 0.5) * 0.5,
                opacity: Math.random() * 0.5 + 0.2,
            });
        }

        const animate = () => {
            ctx.fillStyle = "rgba(11, 11, 11, 0.05)";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            particles.forEach(particle => {
                particle.x += particle.speedX;
                particle.y += particle.speedY;

                // Wrap around edges
                if (particle.x < 0) particle.x = canvas.width;
                if (particle.x > canvas.width) particle.x = 0;
                if (particle.y < 0) particle.y = canvas.height;
                if (particle.y > canvas.height) particle.y = 0;

                ctx.beginPath();
                ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(120, 89, 230, ${particle.opacity})`;
                ctx.fill();
            });

            requestAnimationFrame(animate);
        };

        animate();
    }, []);

    // Mock game data
    const games = [
        { id: "valorant", name: "Valorant", icon: "🎯", activeQuests: 5, color: "#FF4747" },
        { id: "fortnite", name: "Fortnite", icon: "🏗️", activeQuests: 8, color: "#9C27B0" },
        { id: "apex", name: "Apex Legends", icon: "⚡", activeQuests: 4, color: "#FF6B35" },
        { id: "cod", name: "Call of Duty", icon: "🔫", activeQuests: 6, color: "#4CAF50" },
        { id: "overwatch", name: "Overwatch 2", icon: "🛡️", activeQuests: 3, color: "#FF9800" },
    ];

    // Dynamic quest data
    const brandQuests = [
        {
            id: "redbull-clutch",
            brand: "Red Bull",
            title: "CLUTCH KING TOURNAMENT",
            description: "Win 5 rounds with less than 20HP remaining",
            reward: { xp: 5000, fuel: 2000, special: "Exclusive Wings Skin" },
            participants: 28453,
            timeLeft: "48H 23M",
            games: ["valorant", "apex", "cod"],
            featured: true,
            color: "#0033A0",
        },
        {
            id: "monster-mayhem",
            brand: "Monster Energy",
            title: "UNLEASH THE BEAST",
            description: "Get 50 eliminations using ultimate abilities",
            reward: { xp: 3000, fuel: 1500 },
            participants: 15234,
            timeLeft: "5D 12H",
            games: ["apex", "overwatch"],
            color: "#95D600",
        },
    ];

    return (
        <div className="grid-command-alive">
            <canvas ref={canvasRef} className="ambient-canvas" />

            {/* Neural Network Background Effect */}
            <div className="neural-network">
                <div className="network-line network-line--1" />
                <div className="network-line network-line--2" />
                <div className="network-line network-line--3" />
            </div>

            {/* Top Status Bar */}
            <div className="command-header">
                <div className="header-section">
                    <div className="status-indicator">
                        <div className="indicator-core" />
                        <div className="indicator-pulse" />
                    </div>
                    <span className="status-text">COMMAND CENTER</span>
                    <span className="status-tag">ONLINE</span>
                </div>

                <div className="header-section">
                    <div className="data-stream">
                        <span className="stream-label">SYNC</span>
                        <div className="stream-bars">
                            <div className="bar bar--1" />
                            <div className="bar bar--2" />
                            <div className="bar bar--3" />
                            <div className="bar bar--4" />
                        </div>
                        <span className="stream-value">LIVE</span>
                    </div>
                </div>

                <div className="header-section">
                    <button className="notification-hub">
                        <div className="hub-icon">
                            <div className="hub-core">🔔</div>
                            <div className="hub-ring" />
                        </div>
                        <div className="notification-badge">
                            <span>3</span>
                            <div className="badge-pulse" />
                        </div>
                    </button>
                </div>
            </div>

            {/* Main Grid Layout */}
            <div className="command-grid">

                {/* LEFT: Enhanced User Stats */}
                <div className="panel panel--stats">
                    <div className="panel-glow" />

                    <div className="panel__header">
                        <h3 className="glitch-text" data-text="PILOT PROFILE">PILOT PROFILE</h3>
                        <span className="panel__id">#4287</span>
                    </div>

                    <div className="pilot-card">
                        <div className="avatar-container">
                            <div className="avatar-hexagon">
                                <div className="hex-inner">
                                    <div className="hex-content">
                                        <div className="level-display">42</div>
                                    </div>
                                </div>
                                <svg className="hex-svg" viewBox="0 0 200 200">
                                    <defs>
                                        <linearGradient id="hexGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#7859E6" />
                                            <stop offset="100%" stopColor="#FF4238" />
                                        </linearGradient>
                                    </defs>
                                    <path className="hex-path" d="M100,10 L170,50 L170,130 L100,170 L30,130 L30,50 Z" />
                                </svg>
                            </div>
                            <div className="avatar-particles">
                                <div className="particle particle--1" />
                                <div className="particle particle--2" />
                                <div className="particle particle--3" />
                            </div>
                        </div>

                        <div className="pilot-name">
                            <span className="name-prefix">CDR.</span>
                            <span className="name-main">DEATHKILLER99</span>
                        </div>

                        <div className="stat-matrix">
                            <div className="matrix-row">
                                <div className="stat-cell stat-cell--xp">
                                    <div className="cell-background" />
                                    <div className="cell-content">
                                        <span className="stat-value">8,896</span>
                                        <span className="stat-label">TOTAL XP</span>
                                        <div className="stat-trend stat-trend--up">+12%</div>
                                    </div>
                                </div>
                                <div className="stat-cell stat-cell--fuel">
                                    <div className="cell-background" />
                                    <div className="cell-content">
                                        <span className="stat-value">1,058</span>
                                        <span className="stat-label">FUEL</span>
                                        <div className="stat-bar">
                                            <div className="bar-fill" style={{width: "65%"}} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="matrix-row">
                                <div className="stat-cell stat-cell--winrate">
                                    <div className="cell-background" />
                                    <div className="cell-content">
                                        <span className="stat-value">67%</span>
                                        <span className="stat-label">WIN RATE</span>
                                        <div className="stat-graph">
                                            <svg viewBox="0 0 100 40">
                                                <polyline points="0,35 20,30 40,20 60,25 80,10 100,15" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="stat-cell stat-cell--streak">
                                    <div className="cell-background" />
                                    <div className="cell-content">
                                        <span className="stat-value">12</span>
                                        <span className="stat-label">DAY STREAK</span>
                                        <div className="streak-flames">
                                            <span className="flame">🔥</span>
                                            <span className="flame">🔥</span>
                                            <span className="flame">🔥</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="achievement-hologram">
                            <div className="holo-container">
                                <div className="holo-badge holo-badge--new">
                                    <div className="badge-icon">🏆</div>
                                    <div className="badge-info">
                                        <span className="badge-name">SHARPSHOOTER</span>
                                        <span className="badge-rarity">LEGENDARY</span>
                                    </div>
                                    <div className="badge-shine" />
                                </div>
                                <div className="holo-badge">
                                    <div className="badge-icon">⚡</div>
                                    <div className="badge-info">
                                        <span className="badge-name">SPEED DEMON</span>
                                        <span className="badge-rarity">EPIC</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="xp-nexus">
                        <div className="nexus-header">
                            <span className="nexus-label">LEVEL PROGRESSION</span>
                            <span className="nexus-value">LVL 43 IN 2,104 XP</span>
                        </div>
                        <div className="nexus-container">
                            <div className="nexus-track">
                                <div className="nexus-fill" style={{width: "67%"}}>
                                    <div className="nexus-energy" />
                                </div>
                                <div className="nexus-markers">
                                    <div className="marker marker--complete" style={{left: "25%"}} />
                                    <div className="marker marker--complete" style={{left: "50%"}} />
                                    <div className="marker marker--upcoming" style={{left: "75%"}} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guild-command">
                        <div className="guild-badge">
                            <div className="badge-frame">
                                <div className="frame-corner frame-corner--tl" />
                                <div className="frame-corner frame-corner--tr" />
                                <div className="frame-corner frame-corner--bl" />
                                <div className="frame-corner frame-corner--br" />
                            </div>
                            <div className="badge-content">
                                <div className="guild-icon">⚔️</div>
                                <div className="guild-details">
                                    <span className="guild-name">APEX PREDATORS</span>
                                    <span className="guild-rank">RANK #7 GLOBAL</span>
                                </div>
                            </div>
                        </div>
                        <div className="guild-stats">
                            <div className="guild-stat">
                                <span className="stat-icon">👥</span>
                                <span className="stat-text">38/50 ONLINE</span>
                            </div>
                            <div className="guild-stat">
                                <span className="stat-icon">🏆</span>
                                <span className="stat-text">15,234 GP</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* MIDDLE: Mission Control */}
                <div className="panel panel--missions">
                    <div className="panel-scanline" />

                    {/* Enhanced Game Selector */}
                    <div className="game-selector-bar">
                        <button
                            className={`game-filter-btn ${selectedGame === "all" ? "active" : ""}`}
                            onClick={() => setSelectedGame("all")}
                        >
                            <div className="btn-inner">
                                <span className="filter-icon">🎮</span>
                                <span className="filter-label">ALL GAMES</span>
                                <span className="filter-count">26</span>
                            </div>
                            <div className="btn-glow" />
                        </button>

                        <div className="game-pipeline">
                            {selectedGame !== "all" &&
                <div className="pipeline-item">
                    <div className="item-glow" style={{
                        background: `radial-gradient(circle, ${games.find(g => g.id === selectedGame)?.color}40 0%, transparent 70%)`,
                    }} />
                    <span className="item-icon">
                        {games.find(g => g.id === selectedGame)?.icon}
                    </span>
                    <span className="item-name">
                        {games.find(g => g.id === selectedGame)?.name}
                    </span>
                    <button
                        className="item-close"
                        onClick={() => setSelectedGame("all")}
                    >
                        <span>×</span>
                    </button>
                </div>
                            }
                        </div>

                        <button
                            className="game-search-trigger"
                            onClick={() => setShowGameSelector(!showGameSelector)}
                        >
                            <div className="trigger-icon">🔍</div>
                            <div className="trigger-pulse" />
                        </button>
                    </div>

                    {/* Holographic Game Selector */}
                    {showGameSelector &&
            <div className="game-selector-holo">
                <div className="holo-header">
                    <input
                        type="text"
                        placeholder="SEARCH GAMES..."
                        className="holo-search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <div className="search-scanner" />
                </div>
                <div className="holo-grid">
                    {games
                        .filter(game => game.name.toLowerCase().includes(searchQuery.toLowerCase()))
                        .map(game =>
                            <button
                                key={game.id}
                                className={`game-tile ${selectedGame === game.id ? "selected" : ""}`}
                                onClick={() => {
                                    setSelectedGame(game.id);
                                    setShowGameSelector(false);
                                    setSearchQuery("");
                                }}
                            >
                                <div className="tile-bg" style={{
                                    background: `linear-gradient(135deg, ${game.color}20 0%, transparent 100%)`,
                                }} />
                                <div className="tile-content">
                                    <span className="tile-icon">{game.icon}</span>
                                    <span className="tile-name">{game.name}</span>
                                    <span className="tile-quests">{game.activeQuests} QUESTS</span>
                                </div>
                                <div className="tile-border" />
                            </button>)}
                </div>
            </div>
                    }

                    {/* Mission Categories with Energy Flow */}
                    <div className="mission-categories">
                        <div className="category-flow" />
                        <button className="category-tab category-tab--active">
                            <div className="tab-bg" />
                            <span className="tab-icon">🎯</span>
                            <span className="tab-label">GAME QUESTS</span>
                            <span className="tab-count">12</span>
                        </button>
                        <button className="category-tab">
                            <div className="tab-bg" />
                            <span className="tab-icon">🏆</span>
                            <span className="tab-label">BRAND DROPS</span>
                            <span className="tab-count">5</span>
                            <div className="tab-alert" />
                        </button>
                        <button className="category-tab">
                            <div className="tab-bg" />
                            <span className="tab-icon">⚔️</span>
                            <span className="tab-label">GUILD OPS</span>
                            <span className="tab-count">3</span>
                        </button>
                    </div>

                    {/* Mission Grid with Depth */}
                    <div className="mission-grid">

                        {/* Featured Brand Mission */}
                        <div className={`mission-card mission-card--featured ${pulseAnimation ? "pulse" : ""}`}>
                            <div className="featured-energy">
                                <div className="energy-wave energy-wave--1" />
                                <div className="energy-wave energy-wave--2" />
                            </div>

                            <div className="featured-content">
                                <div className="brand-hologram">
                                    <div className="holo-shimmer" />
                                    <span className="brand-name">RED BULL</span>
                                    <span className="brand-tag">EXCLUSIVE DROP</span>
                                </div>

                                <h3 className="mission-title">
                                    <span className="title-main">CLUTCH KING TOURNAMENT</span>
                                    <span className="title-effect" />
                                </h3>

                                <p className="mission-desc">Win 5 rounds with less than 20HP remaining</p>

                                <div className="mission-data">
                                    <div className="data-grid">
                                        <div className="data-cell">
                                            <span className="cell-label">REWARD POOL</span>
                                            <div className="cell-value">
                                                <span className="value-num">5,000</span>
                                                <span className="value-unit">XP</span>
                                            </div>
                                        </div>
                                        <div className="data-cell">
                                            <span className="cell-label">FUEL BONUS</span>
                                            <div className="cell-value">
                                                <span className="value-num">2,000</span>
                                                <span className="value-unit">FUEL</span>
                                            </div>
                                        </div>
                                        <div className="data-cell data-cell--special">
                                            <span className="cell-label">EXCLUSIVE</span>
                                            <div className="cell-value">
                                                <span className="value-icon">🎁</span>
                                                <span className="value-text">WINGS SKIN</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="participation-meter">
                                        <span className="meter-label">28,453 COMPETING</span>
                                        <div className="meter-bar">
                                            <div className="meter-fill" />
                                            <div className="meter-glow" />
                                        </div>
                                    </div>
                                </div>

                                <div className="mission-footer">
                                    <div className="countdown-display">
                                        <div className="countdown-unit">
                                            <span className="unit-value">48</span>
                                            <span className="unit-label">H</span>
                                        </div>
                                        <span className="countdown-separator">:</span>
                                        <div className="countdown-unit">
                                            <span className="unit-value">23</span>
                                            <span className="unit-label">M</span>
                                        </div>
                                    </div>

                                    <button className="action-btn action-btn--primary">
                                        <div className="btn-energy" />
                                        <span className="btn-text">ENTER CHALLENGE</span>
                                        <span className="btn-arrow">→</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Dynamic Mission Cards */}
                        <div className="mission-card">
                            <div className="card-frame">
                                <div className="frame-energy" />
                            </div>

                            <div className="card-header">
                                <div className="game-tag" style={{
                                    background: `linear-gradient(135deg, ${games[0].color}20 0%, transparent 100%)`,
                                }}>
                                    <span className="tag-icon">{games[0].icon}</span>
                                    <span className="tag-name">{games[0].name}</span>
                                </div>
                                <div className="quest-rarity quest-rarity--epic">EPIC</div>
                            </div>

                            <div className="card-body">
                                <h4 className="quest-title">ACE MACHINE</h4>
                                <p className="quest-desc">Get 5 ace rounds in competitive matches</p>

                                <div className="progress-system">
                                    <div className="progress-header">
                                        <span className="progress-label">PROGRESS</span>
                                        <span className="progress-value">2/5</span>
                                    </div>
                                    <div className="progress-track">
                                        <div className="progress-fill" style={{width: "40%"}}>
                                            <div className="fill-glow" />
                                        </div>
                                        <div className="progress-notches">
                                            <div className="notch notch--complete" />
                                            <div className="notch notch--complete" />
                                            <div className="notch" />
                                            <div className="notch" />
                                            <div className="notch" />
                                        </div>
                                    </div>
                                </div>

                                <div className="reward-display">
                                    <div className="reward-item">
                                        <span className="reward-icon">⚡</span>
                                        <span className="reward-value">750 XP</span>
                                    </div>
                                    <div className="reward-item">
                                        <span className="reward-icon">💎</span>
                                        <span className="reward-value">200 FUEL</span>
                                    </div>
                                </div>
                            </div>

                            <button className="card-action">
                                <span className="action-text">CONTINUE</span>
                                <div className="action-shine" />
                            </button>
                        </div>

                        {/* Daily Quest Card */}
                        <div className="mission-card mission-card--daily">
                            <div className="daily-badge">
                                <div className="badge-bg" />
                                <span className="badge-icon">📅</span>
                                <span className="badge-text">DAILY</span>
                            </div>

                            <div className="card-body">
                                <h4 className="quest-title">FIRST BLOOD</h4>
                                <p className="quest-desc">Get the first kill in any match</p>

                                <div className="checklist-matrix">
                                    <div className="matrix-item matrix-item--done">
                                        <div className="item-check">✓</div>
                                        <span className="item-icon">🎯</span>
                                        <span className="item-name">Valorant</span>
                                    </div>
                                    <div className="matrix-item">
                                        <div className="item-check">○</div>
                                        <span className="item-icon">⚡</span>
                                        <span className="item-name">Apex</span>
                                    </div>
                                    <div className="matrix-item">
                                        <div className="item-check">○</div>
                                        <span className="item-icon">🔫</span>
                                        <span className="item-name">COD</span>
                                    </div>
                                </div>

                                <div className="timer-warning">
                                    <div className="warning-icon">⏰</div>
                                    <span className="warning-text">RESETS IN 4H 32M</span>
                                    <div className="warning-pulse" />
                                </div>
                            </div>
                        </div>

                        {/* Guild Mission Card */}
                        <div className="mission-card mission-card--guild">
                            <div className="guild-header">
                                <div className="header-bg" />
                                <span className="header-icon">⚔️</span>
                                <span className="header-text">GUILD OPERATION</span>
                            </div>

                            <div className="card-body">
                                <h4 className="quest-title">FORTRESS DEFENSE</h4>
                                <p className="quest-desc">Defend the guild fortress for 15 waves</p>

                                <div className="squad-status">
                                    <div className="status-ring">
                                        <svg viewBox="0 0 100 100">
                                            <circle cx="50" cy="50" r="45" className="ring-bg" />
                                            <circle cx="50" cy="50" r="45" className="ring-fill"
                                                strokeDasharray="283"
                                                strokeDashoffset="113" />
                                        </svg>
                                        <div className="ring-center">
                                            <span className="center-value">12/15</span>
                                            <span className="center-label">SQUAD</span>
                                        </div>
                                    </div>

                                    <div className="member-preview">
                                        <div className="member-avatar member-avatar--online">S</div>
                                        <div className="member-avatar member-avatar--online">P</div>
                                        <div className="member-avatar member-avatar--away">D</div>
                                        <div className="member-avatar">+9</div>
                                    </div>
                                </div>

                                <button className="card-action card-action--guild">
                                    <span className="action-text">JOIN SQUAD</span>
                                    <div className="action-pulse" />
                                </button>
                            </div>
                        </div>

                    </div>
                </div>

                {/* RIGHT: Activity & Leaderboard */}
                <div className="panel-column">

                    {/* Live Leaderboard */}
                    <div className="panel panel--leaderboard">
                        <div className="panel__header">
                            <h3>LEADERBOARD</h3>
                            <div className="header-tabs">
                                <button className="tab-btn tab-btn--active">TODAY</button>
                                <button className="tab-btn">WEEK</button>
                                <button className="tab-btn">ALL</button>
                            </div>
                        </div>

                        <div className="leaderboard-list">
                            <div className="leader-item leader-item--first">
                                <div className="rank-badge">
                                    <div className="badge-glow" />
                                    <span className="badge-num">1</span>
                                </div>
                                <div className="leader-info">
                                    <span className="leader-name">xXSniperXx</span>
                                    <span className="leader-xp">15,234 XP</span>
                                </div>
                                <div className="leader-trend">🔥</div>
                            </div>

                            <div className="leader-item leader-item--second">
                                <div className="rank-badge">
                                    <span className="badge-num">2</span>
                                </div>
                                <div className="leader-info">
                                    <span className="leader-name">ProGamer42</span>
                                    <span className="leader-xp">14,876 XP</span>
                                </div>
                            </div>

                            <div className="leader-item leader-item--third">
                                <div className="rank-badge">
                                    <span className="badge-num">3</span>
                                </div>
                                <div className="leader-info">
                                    <span className="leader-name">DeadEye</span>
                                    <span className="leader-xp">14,102 XP</span>
                                </div>
                            </div>

                            <div className="leader-separator">
                                <div className="separator-line" />
                                <span className="separator-text">YOUR POSITION</span>
                                <div className="separator-line" />
                            </div>

                            <div className="leader-item leader-item--you">
                                <div className="rank-badge">
                                    <span className="badge-num">42</span>
                                </div>
                                <div className="leader-info">
                                    <span className="leader-name">YOU</span>
                                    <span className="leader-xp">8,896 XP</span>
                                </div>
                                <div className="leader-change">↑ 5</div>
                            </div>
                        </div>
                    </div>

                    {/* Command Center */}
                    <div className="panel panel--command">
                        <div className="panel__header">
                            <h3>COMMAND CENTER</h3>
                        </div>

                        <div className="command-grid-mini">
                            <button className="command-btn command-btn--play">
                                <div className="btn-bg" />
                                <div className="btn-icon">🎮</div>
                                <span className="btn-label">PLAY</span>
                                <div className="btn-energy" />
                            </button>

                            <button className="command-btn command-btn--loot">
                                <div className="btn-bg" />
                                <div className="btn-icon">🎁</div>
                                <span className="btn-label">LOOT</span>
                                <div className="btn-badge">2</div>
                            </button>

                            <button className="command-btn command-btn--shop">
                                <div className="btn-bg" />
                                <div className="btn-icon">🛍️</div>
                                <span className="btn-label">SHOP</span>
                            </button>

                            <button className="command-btn command-btn--guild">
                                <div className="btn-bg" />
                                <div className="btn-icon">⚔️</div>
                                <span className="btn-label">GUILD</span>
                            </button>
                        </div>

                        <div className="event-alert">
                            <div className="alert-bg" />
                            <div className="alert-content">
                                <div className="alert-header">
                                    <span className="alert-icon">⚡</span>
                                    <span className="alert-title">GUILD WAR STARTING</span>
                                </div>
                                <div className="alert-timer">
                                    <span className="timer-label">BEGINS IN</span>
                                    <span className="timer-value">2H 14M</span>
                                </div>
                            </div>
                            <div className="alert-pulse" />
                        </div>
                    </div>

                    {/* Activity Stream */}
                    <div className="panel panel--activity">
                        <div className="panel__header">
                            <h3>ACTIVITY STREAM</h3>
                            <div className="stream-indicator">
                                <div className="indicator-dot" />
                                <span>LIVE</span>
                            </div>
                        </div>

                        <div className="activity-feed">
                            <div className="feed-item feed-item--achievement">
                                <div className="item-timeline">
                                    <div className="timeline-dot" />
                                    <div className="timeline-line" />
                                </div>
                                <div className="item-content">
                                    <span className="item-icon">🏆</span>
                                    <div className="item-details">
                                        <span className="item-text">Unlocked <strong>Clutch Master</strong></span>
                                        <span className="item-time">2 min ago</span>
                                    </div>
                                </div>
                            </div>

                            <div className="feed-item feed-item--quest">
                                <div className="item-timeline">
                                    <div className="timeline-dot" />
                                    <div className="timeline-line" />
                                </div>
                                <div className="item-content">
                                    <span className="item-icon">✅</span>
                                    <div className="item-details">
                                        <span className="item-text">Completed <strong>Daily Triple</strong></span>
                                        <span className="item-time">15 min ago</span>
                                    </div>
                                </div>
                            </div>

                            <div className="feed-item feed-item--reward">
                                <div className="item-timeline">
                                    <div className="timeline-dot" />
                                    <div className="timeline-line" />
                                </div>
                                <div className="item-content">
                                    <span className="item-icon">💎</span>
                                    <div className="item-details">
                                        <span className="item-text">Received <strong>500 FUEL</strong></span>
                                        <span className="item-time">1 hour ago</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default GridCommandAlive;